import type { Ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useCartStore } from '~/stores/cart'
import { UserRole } from '~/types/account'
import type { ProductAPIResponse } from '~/types/shop'

export const usePotmState = (product?: Ref<ProductAPIResponse>) => {
  const { userRole } = useSAuthState()
  const cartStore = useCartStore()
  const { summary } = storeToRefs(cartStore)

  // Computed product to avoid repeating optional chaining
  const currentProduct = computed(() => product?.value)

  // Base product checks
  const isProductOfTheMonth = computed(() =>
    currentProduct.value?.productOfTheMonth
  )

  // Guests (email-only checkout) can't buy POTM either, so they get the same sign-in prompt
  const isAnonymousUser = computed(() =>
    [UserRole.Anonymous, UserRole.Guest].includes(userRole.value)
  )

  // Individual state checks
  const isPotmEligible = computed(() => {
    return summary.value.potm?.isPotmEligible ?? false
  })

  const alreadyPurchased = computed(() => {
    return !!summary.value.apiError?.errors?.find(e => e.code === 'ItemQuantityExceeded')
  })

  // Main logic - determines if POTM auth/restrictions apply
  const needsPotmAuth = computed(() =>
    isProductOfTheMonth.value && isAnonymousUser.value
  )

  // Determines which restriction applies (for authenticated users)
  const potmRestriction = computed(() => {
    if (!isProductOfTheMonth.value) { return null }
    if (isAnonymousUser.value) { return 'unauthenticated' }
    if (alreadyPurchased.value) { return 'alreadyPurchased' }
    if (!isPotmEligible.value) { return 'cartNotQualified' }
    return null // No restrictions - can purchase
  })

  // Derived state for components
  const showPotmComponent = computed(() =>
    isProductOfTheMonth.value && potmRestriction.value !== null
  )

  const potmMessageKey = computed(() => {
    const restriction = potmRestriction.value
    switch (restriction) {
      case 'unauthenticated':
        return 'p.pdp.potm.unauthenticated'
      case 'alreadyPurchased':
        return 'p.pdp.potm.alreadyPurchased'
      case 'cartNotQualified':
        return 'p.pdp.potm.cartNotQualified'
      default:
        return null
    }
  })

  const canPurchasePotm = computed(() =>
    isProductOfTheMonth.value && potmRestriction.value === null
  )

  const showSignInButton = computed(() =>
    potmRestriction.value === 'unauthenticated'
  )

  return {
    // Base checks
    isProductOfTheMonth,
    isAnonymousUser,

    // Individual state checks
    isPotmEligible,
    alreadyPurchased,
    needsPotmAuth,

    // Derived state
    potmRestriction,
    showPotmComponent,
    potmMessageKey,
    canPurchasePotm,
    showSignInButton
  }
}

export type PotmState = ReturnType<typeof usePotmState>
